// Market: set in layout/theme.liquid from the Shopify Markets path ("uk" or "us").
const MARKET = (typeof window !== "undefined" && window.supercoMarket) || "uk"
const IS_US = MARKET === "us"

// Shopify Plus pricing rules (kept here for easy updates).
// Flat fees from Shopify's help centre ('Shopify Plus plan fees by currency and term length', checked
// 27 Oct 2026): GBP £1,800 (3-year term) and £1,950 (1 year); USD $2,300 and $2,500. Shopify doesn't
// publish the variable-fee threshold; $800,000 a month follows the most detailed guide.
// The variable-fee threshold and rates are estimates: Shopify asks higher-volume brands to contact it.
const SHOPIFY_PLUS_TERMS = {
  threeYear: {
    id: "threeYear",
    label: "3-year term",
    thresholdGBP: IS_US ? 800000 : 470000, // Up to this GMV the flat fee applies (in the market's currency)
    flatFeeGBP: IS_US ? 2300 : 1800,
    ratesPercent: {
      online: 0.35,
      retail: 0.25,
      b2b: 0.18,
    },
  },
  oneYear: {
    id: "oneYear",
    label: "1-year term",
    thresholdGBP: IS_US ? 800000 : 470000,
    flatFeeGBP: IS_US ? 2500 : 1950,
    ratesPercent: {
      online: 0.4,
      retail: 0.25,
      b2b: 0.18,
    },
  },
}
const DEFAULT_SHOPIFY_PLAN_TERM = SHOPIFY_PLUS_TERMS.threeYear.id

// Formatting helpers shared with Alpine template
const formatCurrency = (value) => {
  return new Intl.NumberFormat(IS_US ? "en-US" : "en-GB", {
    style: "currency",
    currency: IS_US ? "USD" : "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Format number
const formatNumber = (value) => {
  return new Intl.NumberFormat(IS_US ? "en-US" : "en-GB").format(Math.round(value))
}

// Format percent
const formatPercent = (value) => {
  return `${value.toFixed(2)}%`
}

// Human-readable plan strings for template bindings
const describePlanThreshold = (term) => {
  const threshold = formatCurrency(term.thresholdGBP)
  const flatFee = formatCurrency(term.flatFeeGBP)
  const onlineRate = formatPercent(term.ratesPercent.online)
  return `If GMV ≤ ${threshold}: ${flatFee}/mo. If GMV > ${threshold}: blended rate from channel mix (base online ${onlineRate})`
}

const describeRates = (rates) => {
  const retail = formatPercent(rates.retail)
  const b2b = formatPercent(rates.b2b)
  return `Retail: ${retail} | B2B: ${b2b}`
}

// Expose plan data for Alpine templates
window.tcoShopifyPlanTerms = () => ({
  terms: SHOPIFY_PLUS_TERMS,
  defaultTerm: DEFAULT_SHOPIFY_PLAN_TERM,
  describePlanThreshold,
  describeRates,
})

document.addEventListener("DOMContentLoaded", function () {
  const calculateShopifyPlusLicence = (
    plan,
    monthlyGmv,
    channelMix = { online: 100, retail: 0, b2b: 0 }
  ) => {
    if (!plan) return 0
    if (monthlyGmv <= plan.thresholdGBP) {
      return plan.flatFeeGBP
    }
    const onlineShare = channelMix.online || 0
    const retailShare = channelMix.retail || 0
    const b2bShare = channelMix.b2b || 0
    const blendedRatePercent =
      plan.ratesPercent.online * (onlineShare / 100) +
      plan.ratesPercent.retail * (retailShare / 100) +
      plan.ratesPercent.b2b * (b2bShare / 100)
    return monthlyGmv * (blendedRatePercent / 100)
  }

  const getSelectedPlanTerm = (calculator) => {
    const selected = calculator.querySelector(
      '[data-input="shopifyPlanTerm"]:checked'
    )
    return selected ? selected.value : DEFAULT_SHOPIFY_PLAN_TERM
  }

  // Initialize a single calculator instance
  const initCalculator = (section) => {
    const sectionId = section.dataset.sectionId
    const calculator = section

    // Get all input elements
    const inputs = {
      monthlyGmv: calculator.querySelector('[data-input="monthlyGmv"]'),
      avgOrderValue: calculator.querySelector('[data-input="avgOrderValue"]'),
      channelOnlinePercent: calculator.querySelector(
        '[data-input="channelOnlinePercent"]'
      ),
      channelRetailPercent: calculator.querySelector(
        '[data-input="channelRetailPercent"]'
      ),
      channelB2BPercent: calculator.querySelector(
        '[data-input="channelB2BPercent"]'
      ),
      shopifyPaymentsPercent: calculator.querySelector(
        '[data-input="shopifyPaymentsPercent"]'
      ),
      shopifyPaymentsFixed: calculator.querySelector(
        '[data-input="shopifyPaymentsFixed"]'
      ),
      monthlyAppCost: calculator.querySelector('[data-input="monthlyAppCost"]'),
      monthlyDevCost: calculator.querySelector('[data-input="monthlyDevCost"]'),
      shopifyPlusLicence: calculator.querySelector(
        '[data-input="shopifyPlusLicence"]'
      ),
      useThirdPartyProvider: calculator.querySelector(
        '[data-input="useThirdPartyProvider"]'
      ),
    }
    const shopifyPlanTermInputs = calculator.querySelectorAll(
      '[data-input="shopifyPlanTerm"]'
    )
    const channelMixErrorEl = calculator.querySelector(
      '[data-output="channelMixError"]'
    )

    // Helper function to parse input value, allowing empty strings
    const parseInputValue = (input) => {
      if (input.type === "checkbox") {
        return { value: input.checked, isEmpty: false, isInvalid: false }
      }
      const value = input.value.trim()
      if (value === "") return { value: 0, isEmpty: true }
      const parsed = parseFloat(value)
      if (isNaN(parsed) || parsed < 0) {
        return { value: 0, isEmpty: false, isInvalid: true }
      }
      return { value: parsed, isEmpty: false, isInvalid: false }
    }

    const resetOutputs = () => {
      const outputSelectors = [
        '[data-output="ordersPerMonth"]',
        '[data-output="monthlyTransactionFees"]',
        '[data-output="monthlyAppDevCost"]',
        '[data-output="tcoPercent"]',
        '[data-output="shopifyPercentFee"]',
        '[data-output="shopifyFixedFee"]',
        '[data-output="thirdPartyFee"]',
        '[data-output="monthlyTCO"]',
        '[data-output="annualTCO"]',
        '[data-output="tcoPercentHighlight"]',
        '[data-output="leanMonthlyTCO"]',
        '[data-output="leanAnnualTCO"]',
        '[data-output="leanTcoPercent"]',
        '[data-output="leanTransactionFees"]',
        '[data-output="leanAppDevCost"]',
        '[data-output="typicalMonthlyTCO"]',
        '[data-output="typicalAnnualTCO"]',
        '[data-output="typicalTcoPercent"]',
        '[data-output="typicalTransactionFees"]',
        '[data-output="typicalAppDevCost"]',
        '[data-output="heavyMonthlyTCO"]',
        '[data-output="heavyAnnualTCO"]',
        '[data-output="heavyTcoPercent"]',
        '[data-output="heavyTransactionFees"]',
        '[data-output="heavyAppDevCost"]',
      ]
      outputSelectors.forEach((selector) => {
        const el = calculator.querySelector(selector)
        if (el) {
          el.textContent = "--"
        }
      })
      const tableBody = calculator.querySelector(".tco-calculator__table tbody")
      if (tableBody) tableBody.innerHTML = ""
      if (inputs.shopifyPlusLicence) {
        inputs.shopifyPlusLicence.value = ""
      }
    }

    // Calculate and update all values
    const calculate = () => {
      const monthlyGmvData = parseInputValue(inputs.monthlyGmv)
      const avgOrderValueData = parseInputValue(inputs.avgOrderValue)
      const channelOnlinePercentData = parseInputValue(
        inputs.channelOnlinePercent
      )
      const channelRetailPercentData = parseInputValue(
        inputs.channelRetailPercent
      )
      const channelB2BPercentData = parseInputValue(inputs.channelB2BPercent)
      const shopifyPaymentsPercentData = parseInputValue(
        inputs.shopifyPaymentsPercent
      )
      const shopifyPaymentsFixedData = parseInputValue(
        inputs.shopifyPaymentsFixed
      )
      const monthlyAppCostData = parseInputValue(inputs.monthlyAppCost)
      const monthlyDevCostData = parseInputValue(inputs.monthlyDevCost)
      const useThirdPartyProviderData = parseInputValue(
        inputs.useThirdPartyProvider
      )

      // Use 0 for calculations, but only update input field if it's invalid (negative), not if empty
      let monthlyGmv = monthlyGmvData.value
      let avgOrderValue = avgOrderValueData.value
      let channelOnlinePercent = channelOnlinePercentData.value
      let channelRetailPercent = channelRetailPercentData.value
      let channelB2BPercent = channelB2BPercentData.value
      let shopifyPaymentsPercent = shopifyPaymentsPercentData.value
      let shopifyPaymentsFixed = shopifyPaymentsFixedData.value
      let monthlyAppCost = monthlyAppCostData.value
      let monthlyDevCost = monthlyDevCostData.value
      let useThirdPartyProvider = useThirdPartyProviderData.value

      // Only update input fields if they have invalid (negative) values, not if they're empty
      if (monthlyGmvData.isInvalid && !monthlyGmvData.isEmpty) {
        inputs.monthlyGmv.value = 0
      }
      if (avgOrderValueData.isInvalid && !avgOrderValueData.isEmpty) {
        inputs.avgOrderValue.value = 0
      }
      if (
        channelOnlinePercentData.isInvalid &&
        !channelOnlinePercentData.isEmpty
      ) {
        inputs.channelOnlinePercent.value = 0
      }
      if (
        channelRetailPercentData.isInvalid &&
        !channelRetailPercentData.isEmpty
      ) {
        inputs.channelRetailPercent.value = 0
      }
      if (channelB2BPercentData.isInvalid && !channelB2BPercentData.isEmpty) {
        inputs.channelB2BPercent.value = 0
      }
      if (
        shopifyPaymentsPercentData.isInvalid &&
        !shopifyPaymentsPercentData.isEmpty
      ) {
        inputs.shopifyPaymentsPercent.value = 0
      }
      if (
        shopifyPaymentsFixedData.isInvalid &&
        !shopifyPaymentsFixedData.isEmpty
      ) {
        inputs.shopifyPaymentsFixed.value = 0
      }
      if (monthlyAppCostData.isInvalid && !monthlyAppCostData.isEmpty) {
        inputs.monthlyAppCost.value = 0
      }
      if (monthlyDevCostData.isInvalid && !monthlyDevCostData.isEmpty) {
        inputs.monthlyDevCost.value = 0
      }

      const totalChannelPercent = parseFloat(
        (
          channelOnlinePercent +
          channelRetailPercent +
          channelB2BPercent
        ).toFixed(2)
      )
      const hasValidChannelMix = totalChannelPercent === 100

      if (channelMixErrorEl) {
        if (!hasValidChannelMix) {
          channelMixErrorEl.textContent = `Revenue split must equal 100%. Current total: ${totalChannelPercent.toFixed(
            2
          )}%.`
          channelMixErrorEl.classList.remove("hidden")
          channelMixErrorEl.style.display = "block"
        } else {
          channelMixErrorEl.classList.add("hidden")
          channelMixErrorEl.style.display = "none"
        }
      }

      if (!hasValidChannelMix) {
        resetOutputs()
        return
      }

      const channelMix = {
        online: channelOnlinePercent,
        retail: channelRetailPercent,
        b2b: channelB2BPercent,
      }

      // Determine Shopify Plus licence based on selected term and GMV
      const selectedPlanTerm = getSelectedPlanTerm(calculator)
      const planConfig =
        SHOPIFY_PLUS_TERMS[selectedPlanTerm] ||
        SHOPIFY_PLUS_TERMS[DEFAULT_SHOPIFY_PLAN_TERM]
      const calculatedShopifyPlusLicence = calculateShopifyPlusLicence(
        planConfig,
        monthlyGmv,
        channelMix
      )
      const shopifyPlusLicence = calculatedShopifyPlusLicence
      if (inputs.shopifyPlusLicence) {
        inputs.shopifyPlusLicence.value =
          calculatedShopifyPlusLicence.toFixed(2)
      }

      // Calculate orders per month
      const ordersPerMonth = avgOrderValue > 0 ? monthlyGmv / avgOrderValue : 0

      // Transaction fees breakdown
      const shopifyPercentFee = monthlyGmv * (shopifyPaymentsPercent / 100)
      const shopifyFixedFee = ordersPerMonth * shopifyPaymentsFixed
      const shopifyPaymentsEnabled = useThirdPartyProvider
      const thirdPartyFee = shopifyPaymentsEnabled ? 0 : monthlyGmv * 0.002

      const monthlyTransactionFees =
        shopifyPercentFee + shopifyFixedFee + thirdPartyFee
      const annualTransactionFees = monthlyTransactionFees * 12

      // Total TCO
      const monthlyTCO =
        shopifyPlusLicence +
        monthlyTransactionFees +
        monthlyAppCost +
        monthlyDevCost
      const annualTCO = monthlyTCO * 12
      const tcoPercent = monthlyGmv > 0 ? (monthlyTCO / monthlyGmv) * 100 : 0

      // Update baseline outputs
      const ordersPerMonthEl = calculator.querySelector(
        '[data-output="ordersPerMonth"]'
      )
      const monthlyTransactionFeesEl = calculator.querySelector(
        '[data-output="monthlyTransactionFees"]'
      )
      const monthlyAppDevCostEl = calculator.querySelector(
        '[data-output="monthlyAppDevCost"]'
      )
      const tcoPercentEl = calculator.querySelector(
        '[data-output="tcoPercent"]'
      )
      const shopifyPercentFeeEl = calculator.querySelector(
        '[data-output="shopifyPercentFee"]'
      )
      const shopifyFixedFeeEl = calculator.querySelector(
        '[data-output="shopifyFixedFee"]'
      )
      const thirdPartyFeeEl = calculator.querySelector(
        '[data-output="thirdPartyFee"]'
      )
      const monthlyTCOEl = calculator.querySelector(
        '[data-output="monthlyTCO"]'
      )
      const annualTCOEl = calculator.querySelector('[data-output="annualTCO"]')
      const tcoPercentHighlightEl = calculator.querySelector(
        '[data-output="tcoPercentHighlight"]'
      )

      if (ordersPerMonthEl)
        ordersPerMonthEl.textContent = formatNumber(ordersPerMonth)
      if (monthlyTransactionFeesEl)
        monthlyTransactionFeesEl.textContent = formatCurrency(
          monthlyTransactionFees
        )
      if (monthlyAppDevCostEl)
        monthlyAppDevCostEl.textContent = formatCurrency(
          monthlyAppCost + monthlyDevCost
        )
      if (tcoPercentEl) tcoPercentEl.textContent = formatPercent(tcoPercent)
      if (shopifyPercentFeeEl)
        shopifyPercentFeeEl.textContent = formatCurrency(shopifyPercentFee)
      if (shopifyFixedFeeEl)
        shopifyFixedFeeEl.textContent = formatCurrency(shopifyFixedFee)
      if (thirdPartyFeeEl) {
        thirdPartyFeeEl.textContent = formatCurrency(thirdPartyFee)
        // Highlight when additional third-party fee is applied (Shopify Payments disabled)
        if (!shopifyPaymentsEnabled) {
          thirdPartyFeeEl.classList.remove("text-gray-400")
          thirdPartyFeeEl.classList.add("text-accent-yellow")
        } else {
          thirdPartyFeeEl.classList.remove("text-accent-yellow")
          thirdPartyFeeEl.classList.add("text-gray-400")
        }
      }
      if (monthlyTCOEl) monthlyTCOEl.textContent = formatCurrency(monthlyTCO)
      if (annualTCOEl) annualTCOEl.textContent = formatCurrency(annualTCO)
      if (tcoPercentHighlightEl)
        tcoPercentHighlightEl.textContent = formatPercent(tcoPercent)

      // Calculate scenarios
      // Lean scenario: lower app/dev costs, no third-party
      const leanAppCost = monthlyAppCost * 0.5
      const leanDevCost = monthlyDevCost * 0.5
      const leanTransactionFees = shopifyPercentFee + shopifyFixedFee
      const leanMonthlyTCO =
        shopifyPlusLicence + leanTransactionFees + leanAppCost + leanDevCost
      const leanAnnualTCO = leanMonthlyTCO * 12
      const leanTcoPercent =
        monthlyGmv > 0 ? (leanMonthlyTCO / monthlyGmv) * 100 : 0

      // Typical scenario: user inputs
      const typicalMonthlyTCO = monthlyTCO
      const typicalAnnualTCO = annualTCO
      const typicalTcoPercent = tcoPercent
      const typicalTransactionFees = monthlyTransactionFees
      const typicalAppDevCost = monthlyAppCost + monthlyDevCost

      // Heavy scenario: Same GMV but higher operational complexity
      const heavyAppCost = monthlyAppCost * 2
      const heavyDevCost = monthlyDevCost * 2
      const heavyTransactionFees = monthlyTransactionFees + ordersPerMonth * 0.5 // Higher processing complexity
      const heavyMonthlyTCO =
        shopifyPlusLicence + heavyTransactionFees + heavyAppCost + heavyDevCost
      const heavyAnnualTCO = heavyMonthlyTCO * 12
      const heavyTcoPercent =
        monthlyGmv > 0 ? (heavyMonthlyTCO / monthlyGmv) * 100 : 0

      // Update lean scenario outputs
      const leanMonthlyTCOEl = calculator.querySelector(
        '[data-output="leanMonthlyTCO"]'
      )
      const leanAnnualTCOEl = calculator.querySelector(
        '[data-output="leanAnnualTCO"]'
      )
      const leanTcoPercentEl = calculator.querySelector(
        '[data-output="leanTcoPercent"]'
      )
      const leanTransactionFeesEl = calculator.querySelector(
        '[data-output="leanTransactionFees"]'
      )
      const leanAppDevCostEl = calculator.querySelector(
        '[data-output="leanAppDevCost"]'
      )

      if (leanMonthlyTCOEl)
        leanMonthlyTCOEl.textContent = formatCurrency(leanMonthlyTCO)
      if (leanAnnualTCOEl)
        leanAnnualTCOEl.textContent = formatCurrency(leanAnnualTCO)
      if (leanTcoPercentEl)
        leanTcoPercentEl.textContent = formatPercent(leanTcoPercent)
      if (leanTransactionFeesEl)
        leanTransactionFeesEl.textContent = formatCurrency(leanTransactionFees)
      if (leanAppDevCostEl)
        leanAppDevCostEl.textContent = formatCurrency(leanAppCost + leanDevCost)

      // Update typical scenario outputs
      const typicalMonthlyTCOEl = calculator.querySelector(
        '[data-output="typicalMonthlyTCO"]'
      )
      const typicalAnnualTCOEl = calculator.querySelector(
        '[data-output="typicalAnnualTCO"]'
      )
      const typicalTcoPercentEl = calculator.querySelector(
        '[data-output="typicalTcoPercent"]'
      )
      const typicalTransactionFeesEl = calculator.querySelector(
        '[data-output="typicalTransactionFees"]'
      )
      const typicalAppDevCostEl = calculator.querySelector(
        '[data-output="typicalAppDevCost"]'
      )

      if (typicalMonthlyTCOEl)
        typicalMonthlyTCOEl.textContent = formatCurrency(typicalMonthlyTCO)
      if (typicalAnnualTCOEl)
        typicalAnnualTCOEl.textContent = formatCurrency(typicalAnnualTCO)
      if (typicalTcoPercentEl)
        typicalTcoPercentEl.textContent = formatPercent(typicalTcoPercent)
      if (typicalTransactionFeesEl)
        typicalTransactionFeesEl.textContent = formatCurrency(
          typicalTransactionFees
        )
      if (typicalAppDevCostEl)
        typicalAppDevCostEl.textContent = formatCurrency(typicalAppDevCost)

      // Update heavy scenario outputs
      const heavyMonthlyTCOEl = calculator.querySelector(
        '[data-output="heavyMonthlyTCO"]'
      )
      const heavyAnnualTCOEl = calculator.querySelector(
        '[data-output="heavyAnnualTCO"]'
      )
      const heavyTcoPercentEl = calculator.querySelector(
        '[data-output="heavyTcoPercent"]'
      )
      const heavyTransactionFeesEl = calculator.querySelector(
        '[data-output="heavyTransactionFees"]'
      )
      const heavyAppDevCostEl = calculator.querySelector(
        '[data-output="heavyAppDevCost"]'
      )

      if (heavyMonthlyTCOEl)
        heavyMonthlyTCOEl.textContent = formatCurrency(heavyMonthlyTCO)
      if (heavyAnnualTCOEl)
        heavyAnnualTCOEl.textContent = formatCurrency(heavyAnnualTCO)
      if (heavyTcoPercentEl)
        heavyTcoPercentEl.textContent = formatPercent(heavyTcoPercent)
      if (heavyTransactionFeesEl)
        heavyTransactionFeesEl.textContent =
          formatCurrency(heavyTransactionFees)
      if (heavyAppDevCostEl)
        heavyAppDevCostEl.textContent = formatCurrency(
          heavyAppCost + heavyDevCost
        )

      // Update comparison table
      const comparisonData = [
        {
          name: "Lean",
          type: "lean",
          gmv: monthlyGmv,
          orders: ordersPerMonth,
          transactionFees: leanTransactionFees,
          monthlyTCO: leanMonthlyTCO,
          annualTCO: leanAnnualTCO,
          tcoPercent: leanTcoPercent,
        },
        {
          name: "Typical",
          type: "typical",
          gmv: monthlyGmv,
          orders: ordersPerMonth,
          transactionFees: typicalTransactionFees,
          monthlyTCO: typicalMonthlyTCO,
          annualTCO: typicalAnnualTCO,
          tcoPercent: typicalTcoPercent,
        },
        {
          name: "Heavy",
          type: "heavy",
          gmv: monthlyGmv,
          orders: ordersPerMonth,
          transactionFees: heavyTransactionFees,
          monthlyTCO: heavyMonthlyTCO,
          annualTCO: heavyAnnualTCO,
          tcoPercent: heavyTcoPercent,
        },
      ]

      const tableBody = calculator.querySelector(".tco-calculator__table tbody")
      if (tableBody) {
        tableBody.innerHTML = comparisonData
          .map((scenario) => {
            const isHighlighted = scenario.type === "typical"
            const badgeClass = `tco-calculator__badge--${scenario.type}`

            return `
              <tr class="${
                isHighlighted ? "tco-calculator__row--highlighted" : ""
              }">
                <td class="tco-calculator__cell--name">
                  <span class="tco-calculator__badge ${badgeClass} font-body text-xs font-medium px-3 py-1 rounded-full inline-block">
                    ${scenario.name}
                  </span>
                </td>
                <td>${formatCurrency(scenario.gmv)}</td>
                <td>${formatNumber(scenario.orders)}</td>
                <td>${formatCurrency(scenario.transactionFees)}</td>
                <td>${formatCurrency(scenario.monthlyTCO)}</td>
                <td>${formatCurrency(scenario.annualTCO)}</td>
                <td style="${
                  isHighlighted
                    ? "color: rgb(var(--color-accents-green)) !important;"
                    : ""
                }">${formatPercent(scenario.tcoPercent)}</td>
              </tr>
            `
          })
          .join("")
      }
    }

    // Handle blur event to enforce minimum value when field loses focus
    const handleBlur = (input) => {
      if (input.type === "checkbox") return
      const value = input.value.trim()
      if (value === "" || isNaN(parseFloat(value)) || parseFloat(value) < 0) {
        input.value = 0
        calculate()
      }
    }

    // Add event listeners to all inputs
    Object.values(inputs).forEach((input) => {
      if (input) {
        if (input.type === "checkbox") {
          input.addEventListener("change", calculate)
        } else {
          input.addEventListener("input", calculate)
          input.addEventListener("change", calculate)
          input.addEventListener("blur", () => handleBlur(input))
        }
      }
    })

    shopifyPlanTermInputs.forEach((input) => {
      input.addEventListener("change", calculate)
    })

    // Add click handler for checkbox wrapper
    const checkboxWrapper = calculator.querySelector("[data-checkbox-wrapper]")
    if (checkboxWrapper) {
      checkboxWrapper.addEventListener("click", (e) => {
        if (e.target !== inputs.useThirdPartyProvider) {
          inputs.useThirdPartyProvider.checked =
            !inputs.useThirdPartyProvider.checked
          calculate()
        }
      })
    }

    // Initial calculation
    calculate()
  }

  // Initialize all calculator sections on the page
  const calculatorSections = document.querySelectorAll(".tco-calculator")
  calculatorSections.forEach(initCalculator)
})
